import { _decorator } from 'cc';
import StateModel from './StateModel';
import StateScenario from './StateScenario';
import StateContextDependObject from './StateContextDependObject';
import BackgroundSleeper from '../../Components/Misc/BackgroundSleeper';

const { ccclass, property } = _decorator;

@ccclass('StateContext')
export default abstract class StateContext extends BackgroundSleeper {
    @property([StateContextDependObject])
    protected readonly contextDependObjects: StateContextDependObject[] = [];

    private _model: StateModel = null;
    private _states: { [stateName: string]: StateScenario } = {};
    private _stateFlow: string[] = [];
    private _currentState: StateScenario = null;
    private _currentStateIndex = -1;
    private _lastRoundID = -1;

    public get model() {
        return this._model;
    }
    public set model(value: StateModel) {
        if (this._model === value) {
            return;
        }
        if (this._model) {
            this.detachStateModel();
        }
        if (value) {
            this.attachStateModel(value);
        }
    }

    protected get currentState() {
        return this._currentState;
    }
    protected set currentState(newState: StateScenario) {
        if (this._currentState === newState) {
            return;
        }
        this._currentState = newState;
        this._currentStateIndex = this._stateFlow.findIndex((state) => this._states[state] === newState);
    }

    public initContext(states: { [stateName: string]: StateScenario }, stateFlow: string[]) {
        this._states = states;
        this._stateFlow = stateFlow;
    }

    protected attachStateModel(model: StateModel) {
        this._model = model;
        this.contextDependObjects.forEach((object) => object.attachContext(this));
        if (this.awake && this.node.activeInHierarchy) {
            this.contextDependObjects.forEach((object) => object.tryRegisterContextEvents());
        }
    }

    protected detachStateModel() {
        if (!this._model) {
            return;
        }
        this._resetContext();
        this.contextDependObjects.forEach((object) => object.tryUnregisterContextEvents());
        this.contextDependObjects.forEach((object) => object.detachContext());
        this._model = null;
    }

    protected registerAwakeCallback() {
        this.contextDependObjects.forEach((object) => object.tryRegisterContextEvents());
        this._model.on(StateModel.EVENT_TYPE.NEW_EVENT_RECEIVED, this._onNewEventReceived, this);
        this._model.on(StateModel.EVENT_TYPE.STATE_CHANGED, this._onStateChanged, this);
    }

    protected unregisterAwakeCallback(): void {
        this.contextDependObjects.forEach((object) => object.tryUnregisterContextEvents());
        // BackgroundSleeper.onDestroy() 無條件呼叫這裡，此時 model 可能已經在 detachStateModel() 被清成 null（例如離場後、node 被快取，之後才真的 destroy）
        this._model?.off(StateModel.EVENT_TYPE.NEW_EVENT_RECEIVED, this._onNewEventReceived, this);
        this._model?.off(StateModel.EVENT_TYPE.STATE_CHANGED, this._onStateChanged, this);
    }

    protected onContextReset(): void {}

    protected onAwake() {
        this.contextDependObjects.forEach((object) => object.onContextAwake());
        this._jumpToState(this._model.state, this._model.roundID && this._model.roundID === this._lastRoundID);
    }

    protected onSleep() {
        this.contextDependObjects.forEach((object) => object.onContextSleep());
        this._lastRoundID = this._model.roundID;
    }

    private _goToState(targetStateName: string) {
        if (this.currentState && this.currentState === this._states[targetStateName]) {
            return;
        }
        this.currentState?.exit();
        this.currentState = this._states[targetStateName];
        this.currentState.enter();
    }

    private _jumpToState(targetStateName: string, continueFromCurrent: boolean) {
        if (!continueFromCurrent) {
            this._resetContext();
        }

        if (!this._stateFlow.includes(targetStateName)) {
            this.currentState = this._states[targetStateName];
            this.currentState.enter();
        } else {
            const targetIndex = this._stateFlow.findIndex((state) => state === targetStateName);
            let startIndex = Math.max(this._currentStateIndex, 0);
            if (targetIndex < startIndex || this._currentStateIndex === -1) {
                this._resetContext();
                startIndex = 0;
            }
            for (let i = startIndex; i < this._stateFlow.length; i++) {
                this.currentState = this._states[this._stateFlow[i]];
                if (i === targetIndex) {
                    this.currentState.enter();
                    return;
                }
                this.currentState.skip();
            }
        }
    }

    private _resetContext() {
        if (!this.currentState) {
            return;
        }
        this.currentState.exit();
        this.currentState = null;
        this.onContextReset();
    }

    private _onNewEventReceived() {
        if (this.currentState?.isCurrent) {
            this.currentState.progress();
        }
    }

    private _onStateChanged() {
        if (!this.currentState) {
            this._jumpToState(this._model.state, false);
        } else {
            this._goToState(this._model.state);
        }
    }
}
